import {
    Card,
    Col,
    Form,
    FormButtonRow,
    FormGroup,
    FormParagraph,
    FormSection,
    IconButton,
    PageHeader,
    Row,
    Text,
    TextInput,
} from '@ksyos/design-system';
import { useParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IWaitingTime, IWaitingTimeWithAuthorization } from '@frontend/components/interfaces';
import type { IReduxState } from '@frontend/configureStore';
import type { ValidationErrors } from '@frontend/utils/validation';
import { ManagerAuthorization } from '@common/enums';
import { serviceOrganizationSettingWaitingTimeSchema } from '@common/validation/serviceOrganizationSetting';
import { fetchWaitingTimes } from '@frontend/components/pages/organizations/utils/waitingTimesUtils';
import { CancelButton } from '@frontend/components/userDataManagement/CancelButton';
import { SaveButton } from '@frontend/components/userDataManagement/SaveButton';
import { getSelectedLanguage } from '@frontend/translate';
import { assertHasAuthorization, hasOneAuthorization, useAbortableFetch } from '@frontend/utils';
import { getValidationErrorTranslation } from '@frontend/utils/getValidationErrorTranslation';
import { emptyValidationErrors, validate } from '@frontend/utils/validation';

type WaitingTimesValidationErrors = {
    [waitingTimeId: string]: ValidationErrors;
};

export function OrganizationWaitingTimes() {
    const { organizationId } = useParams() as { organizationId: string };
    const translate = getSelectedLanguage();

    const isManager = useSelector((state: IReduxState) => state.authentication.isManager);

    if (isManager) {
        assertHasAuthorization(ManagerAuthorization.organizationRead);
    }

    const [originalWaitingTimes, setOriginalWaitingTimes] = useState<IWaitingTimeWithAuthorization[]>([]);
    const [waitingTimes, setWaitingTimes] = useState<IWaitingTimeWithAuthorization[]>([]);
    const [errors, setErrors] = useState<WaitingTimesValidationErrors>({});
    const [canEdit, setCanEdit] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const abortableFetch = useAbortableFetch();

    useEffect(() => {
        loadWaitingTimes();
    }, [organizationId]);

    if (waitingTimes.length === 0) {
        return null;
    }

    return (
        <>
            <PageHeader title={translate.waitingTime.manageWaitingTimes} />
            <Text mb={6}>{translate.waitingTime.explanation}</Text>
            <Card>
                <Card.Transition type={isEditing ? 'edit' : 'read'} />
                <Card.Header
                    action={
                        !isEditing && canEdit ? (
                            <IconButton
                                icon="edit-light"
                                labelText="edit"
                                onClick={() => setIsEditing(true)}
                            />
                        ) : undefined
                    }
                    title={translate.waitingTime.waitingTimeForReferrals}
                />
                <Card.Body>
                    <Form submitOnEnter={false}>
                        {waitingTimes.map((waitingTime) => (
                            <FormSection
                                key={waitingTime.id}
                                title={waitingTime.service.name}
                            >
                                {renderFormGroup(waitingTime, 'applicationWaitingTime')}
                                {renderFormGroup(waitingTime, 'treatmentWaitingTime')}
                            </FormSection>
                        ))}

                        {isEditing && (
                            <FormButtonRow>
                                <CancelButton
                                    disabled={isSaving}
                                    onCancel={handleCancel}
                                />
                                <SaveButton
                                    disabled={isSaving}
                                    onSave={handleSubmit}
                                />
                            </FormButtonRow>
                        )}
                    </Form>
                </Card.Body>
            </Card>
        </>
    );

    function renderFormGroup(waitingTime: IWaitingTime, fieldKey: 'applicationWaitingTime' | 'treatmentWaitingTime') {
        return (
            <FormGroup
                hasErrors={errors[waitingTime.id]?.has(fieldKey)}
                label={translate.waitingTime[fieldKey]}
            >
                {isEditing && (
                    <Row columnGap={[2, null, null, 4]}>
                        <Col style={{ width: '80px' }}>
                            <TextInput
                                error={getValidationErrorTranslation(errors[waitingTime.id]?.get(fieldKey))}
                                onChange={(e) =>
                                    handleChange(
                                        waitingTime.id,
                                        fieldKey,
                                        !e.target.value || isNaN(+e.target.value) ? null : +e.target.value
                                    )
                                }
                                value={waitingTime[fieldKey] !== null ? `${waitingTime[fieldKey]}` : ''}
                            />
                        </Col>
                        <Col>
                            <Text mt={2}>
                                {waitingTime[fieldKey] === 1 ? translate.waitingTime.week : translate.waitingTime.weeks}
                            </Text>
                        </Col>
                    </Row>
                )}
                {!isEditing && (
                    <FormParagraph>
                        {waitingTime[fieldKey] !== null
                            ? `${waitingTime[fieldKey]} ${waitingTime[fieldKey] === 1 ? translate.waitingTime.week : translate.waitingTime.weeks}`
                            : '-'}
                    </FormParagraph>
                )}
            </FormGroup>
        );
    }

    function handleChange<K extends keyof IWaitingTime>(waitingTimeId: string, name: K, value: IWaitingTime[K]) {
        setWaitingTimes(
            waitingTimes.map((waitingTime) => {
                if (waitingTime.id === waitingTimeId) {
                    return {
                        ...waitingTime,
                        [name]: value,
                    };
                }

                return waitingTime;
            })
        );
    }

    async function loadWaitingTimes() {
        const result = await fetchWaitingTimes(organizationId);

        setCanEdit(
            hasOneAuthorization(ManagerAuthorization.organizationAddEdit) ||
                result.filter(
                    (waitingTime: { authorization: { canUpdateWaitingTime: boolean } }) =>
                        waitingTime.authorization.canUpdateWaitingTime
                ).length > 0
        );

        setOriginalWaitingTimes(result);
        setWaitingTimes(result);
        resetValidationErrors(result);
    }

    async function handleCancel() {
        setIsEditing(false);
        setWaitingTimes(originalWaitingTimes);
        resetValidationErrors(waitingTimes);
    }

    async function handleSubmit() {
        setIsSaving(true);

        const requests = [];
        for (const waitingTime of waitingTimes) {
            const { errors: validationErrors, item } = validate(
                serviceOrganizationSettingWaitingTimeSchema,
                waitingTime
            );

            if (validationErrors.size > 0) {
                setErrors({ ...errors, [waitingTime.id]: validationErrors });
            } else {
                const url = `/api/organizations/${organizationId}/waitingTimes/${waitingTime.id}`;
                requests.push(abortableFetch.fetchJson(url, 'POST', item));
            }
        }

        if (requests.length === waitingTimes.length) {
            await Promise.all(requests);
            await loadWaitingTimes();
            setIsEditing(false);
        }

        setIsSaving(false);
    }

    function resetValidationErrors(waitingTimeItems: IWaitingTime[]) {
        setErrors(
            waitingTimeItems.reduce(
                (prev: WaitingTimesValidationErrors, cur: IWaitingTime) => ({
                    ...prev,
                    [cur.id]: emptyValidationErrors,
                }),
                {}
            )
        );
    }
}
